import React, { useMemo } from 'react';
import { Box, Flex } from '@qga/roo-ui/components';
import PropTypes from 'prop-types';

import StarRating from '@shared/v2/components/StarRating';
import getSuburbName from '@shared/v2/utils/getSuburbName';

import Link from '@/components/Link';
import PropertyTags from '@/components/PropertyTags';
import Summary from '@/components/Summary';
import HolidaysIcon from '@/shared/components/HolidaysIcon';
import getPropertyFromDestination from '@/utils/getPropertyFromDestination';
import getTagsFromPropertyContent from '@/utils/getTagsFromPropertyContent';
import PropertyContent from '../../propTypes/PropertyContent';

const NAME_FONT_SIZE_MAP = {
  base: ['base', null, 'lg'],
  sm: ['sm', null, 'md'],
};

const TAGLINE_FONT_SIZE_MAP = {
  base: ['sm', null, 'base'],
  sm: ['xs', null, 'sm'],
};

const PropertyTitle = ({ propertyContent, size = 'base', href }) => {
  const { name, rating, ratingType, suburb, destination } = propertyContent;

  const tagline = useMemo(() => {
    if (propertyContent.tagline) {
      return propertyContent.tagline;
    }

    const property = getPropertyFromDestination(propertyContent);

    return property && property.tagline;
  }, [propertyContent]);

  return (
    <Box>
      <Box>
        <Summary
          text={getSuburbName(suburb, destination.title)}
          prefix={<HolidaysIcon />}
        />
      </Box>
      <Link.Naked href={href}>
        <Box
          fontSize={NAME_FONT_SIZE_MAP[size]}
          fontWeight="bold"
          pt={[0, null, 3]}
        >
          {name}
        </Box>
        <Flex
          alignItems={[null, null, 'center']}
          flexDirection={['column', null, 'row']}
          my={3}
        >
          <StarRating rating={rating} ratingType={ratingType} size={14} />
          {propertyContent && getTagsFromPropertyContent(propertyContent) && (
            <Box ml={[null, null, 2]} py={[2, null, 0]}>
              <PropertyTags
                tags={getTagsFromPropertyContent(propertyContent)}
              />
            </Box>
          )}
        </Flex>
        {tagline && (
          <Box
            mt="6"
            color="greys.steel"
            fontSize={TAGLINE_FONT_SIZE_MAP[size]}
          >
            {tagline}
          </Box>
        )}
      </Link.Naked>
    </Box>
  );
};

PropertyTitle.propTypes = {
  propertyContent: PropTypes.shape(PropertyContent).isRequired,
  size: PropTypes.oneOf(['xs', 'sm', 'base']),
  href: PropTypes.oneOfType([PropTypes.string, PropTypes.object]).isRequired,
};

export default PropertyTitle;
